import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-2f0c4077"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "inline-config-container" }
const _hoisted_2 = { class: "modal-header" }
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", null, "Configuration Editor", -1))
const _hoisted_4 = { class: "modal-body" }
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { key: 1 }
const _hoisted_7 = { class: "dropdown-wrapper" }
const _hoisted_8 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("label", null, "Node Type:", -1))
const _hoisted_9 = ["value"]
const _hoisted_10 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("option", { value: "None" }, "None", -1))
const _hoisted_11 = ["value"]
const _hoisted_12 = { key: 0 }
const _hoisted_13 = { key: 1 }

import { ref, computed, onMounted, watch } from 'vue';
import DynamicFormField from './DynamicFormField.vue';
import type { BpmnNode } from './types/bpmn';

interface ConfigItem {
  id: string;
  subtype: string;
  [key: string]: any;
}

// Caution these two interfaces are duplicated from server's TypesAnalyzer.ts
// TODO: move to a shared location
// ******************************************
interface DictionaryMetadata {
  keyType: string;
  value: PropertyMetadata;
}

interface PropertyMetadata {
  name: string;
  type: string;
  required: boolean;
  isArray: boolean;
  isEnum: boolean;
  enumValues?: string[];
  childProps?: Record<string, PropertyMetadata>;
  dictionary?: DictionaryMetadata;

  // fields derived from TSDoc comments:
  hidden?: boolean;
  defaultValue?: any;
  enumOptions?: string[];
  uiControl?: string;
  collapsed?: boolean;
}
// ******************************************

interface HeaderConfig {
  taskTypes: Record<string, string>;
  gateTypes: Record<string, string>;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ConfigEditorModal',
  props: {
  processorService: {
    type: Object,
    required: true,
  },
  selectedNode: {
    type: Object as () => BpmnNode | null,
    default: null,
  },
  show: Boolean,
  inlineMode: {
    type: Boolean,
    default: false
  }
},
  emits: ['close', 'save', 'config-changed'],
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

// Our config array loaded at mount:
const configItems = ref<ConfigItem[]>([]);
const metadata = ref<Record<string, PropertyMetadata[]>>({});
const header = ref<HeaderConfig>({ taskTypes: {}, gateTypes: {} });

// Called by DevChat on mount or inline usage, initially loads config...
onMounted(async () => {
  await loadConfigFromServer();
});

async function loadConfigFromServer() {
  const configDataText = await props.processorService.getConfigData();
  const configData = JSON.parse(configDataText.formConfig);

  configItems.value = configData.config;
  header.value = configData.header;
  metadata.value = configData.metadata;
  
  // Apply default values to config items for boolean fields if they're undefined
  applyDefaultValues();
}

// Apply default values to config items - only for undefined/null fields
function applyDefaultValues() {
  if (!configItems.value || !metadata.value) return;
  
  for (const item of configItems.value) {
    const configTypeKey = findConfigTypeKeyForSubtype(item.subtype);
    if (!configTypeKey) continue;
    
    const fields = metadata.value[configTypeKey] || [];
    for (const field of fields) {
      // Only apply defaults to undefined/null fields, not to false values
      if (field.defaultValue !== undefined && (item[field.name] === undefined || item[field.name] === null)) {
        console.log(`Applying default value ${field.defaultValue} to ${field.name} for item ${item.id}`);
        item[field.name] = field.defaultValue;
      }
    }
  }
}

// 1) The new method to load default config data.
async function loadDefaultConfig() {
  try {
    const defaultConfigDataText = await props.processorService.getDefaultConfigData();
    const configData = JSON.parse(defaultConfigDataText.formConfig);

    configItems.value = configData.config;
    header.value = configData.header;
    metadata.value = configData.metadata;
    
    // Apply default values from metadata to config items
    applyDefaultValues();

    console.log('[ConfigEditorModal] Loaded default config data successfully.');
    emit('config-changed'); // Let parent know we've changed the config
  } catch (error) {
    console.error('Error loading default config data:', error);
  }
}

// All subtype mappings from header
const allSubtypes = computed(() => ({
  // Task subtypes
  ...(header.value.taskTypes || {}),
  // Gate subtypes
  ...(header.value.gateTypes || {})
}));


// Find the config item for currently selected node, if any
const currentItem = computed(() => {
  if (!props.selectedNode) return null;
  // Just find existing config item, don't create new ones here
  return configItems.value.find((c) => c.id === props.selectedNode?.id) || null;
});

// Watch for selected node changes and create new config items if needed
watch(() => props.selectedNode, (newNode) => {
  if (!newNode) return;
  
  // Check if we already have a config item for this node
  const existingItem = configItems.value.find((c) => c.id === newNode.id);
  if (!existingItem) {
    // Create a new config item for this node
    const newItem = {
      id: newNode.id,
      subtype: 'None'
    };
    configItems.value.push(newItem);
  }
}, { immediate: true });

// If we have a matched item, use that subtype, else "None"
const currentSubtype = computed(() => {
  return currentItem.value?.subtype || 'None';
});

// Based on currentSubtype, get metadata fields
function findConfigTypeKeyForSubtype(subtype: string): string | null {
  // Example logic searching in your header, as shown in your code
  for (const [configTypeKey, subTypeVal] of Object.entries(header.value.taskTypes)) {
    if (subTypeVal === subtype) return configTypeKey;
  }
  for (const [configTypeKey, subTypeVal] of Object.entries(header.value.gateTypes)) {
    if (subTypeVal === subtype) return configTypeKey;
  }
  return null;
}

const currentItemFields = computed(() => {
  const configTypeKey = findConfigTypeKeyForSubtype(currentSubtype.value);
  if (!configTypeKey) return [];
  // Return the field definitions from metadata
  return metadata.value[configTypeKey] || [];
});

// When the user picks a subtype from the dropdown
function onSubtypeChanged(e: Event) {
  const newSubtype = (e.target as HTMLSelectElement).value;
  const nodeId = props.selectedNode?.id;
  if (!nodeId) return;

  // Check if we already have a config item for this node
  const existingItem = configItems.value.find((c) => c.id === nodeId);

  if (existingItem) {
    // If user picks "None," remove this config entry
    if (newSubtype === 'None') {
      const idx = configItems.value.indexOf(existingItem);
      if (idx !== -1) configItems.value.splice(idx, 1);
    } else {
      // Otherwise just update the subtype
      existingItem.subtype = newSubtype;
      // Optionally fill missing fields with new defaults
      const defaults = createDefaultConfigForSubtype(newSubtype, metadata.value);
      for (const key in defaults) {
        if (existingItem[key] === undefined) {
          existingItem[key] = defaults[key];
        }
      }
    }
  } else {
    // If no config item exists, create a brand-new one if not "None"
    if (newSubtype !== 'None') {
      const newCfg = createDefaultConfigForSubtype(newSubtype, metadata.value);
      // Set the real ID to the node's ID
      newCfg.id = nodeId;
      configItems.value.push(newCfg);
    }
  }

  // We let the parent know config changed
  emit('config-changed');
}

/**
 * Recursively builds an object from a given set of property definitions
 */
function createObjectFromPropertyDefs(
  propertyDefs: PropertyMetadata[]
): Record<string, any> {
  const result: Record<string, any> = {};

  for (const def of propertyDefs) {
    const propName = def.name;

    // Use doc-comment default if present
    if (def.defaultValue !== undefined) {
      result[propName] = def.defaultValue;
      continue;
    }

    // If it's an array
    if (def.isArray) {
      result[propName] = [];
      continue;
    }

    // If it's a dictionary
    if (def.dictionary) {
      result[propName] = {};
      continue;
    }

    // If it's an object with childProps, build recursively
    if (def.childProps && Object.keys(def.childProps).length > 0) {
      const nestedProps = Object.values(def.childProps);
      result[propName] = createObjectFromPropertyDefs(nestedProps);
      continue;
    }

    // If it's an enum
    if (def.isEnum && def.enumValues && def.enumValues.length > 0) {
      result[propName] = def.enumValues[0];
      continue;
    }

    // Otherwise fallback to typed defaults
    switch (def.type) {
      case 'string':  result[propName] = ''; break;
      case 'number':  result[propName] = 0;  break;
      case 'boolean': result[propName] = def.defaultValue !== undefined ? def.defaultValue : false; break;
      default:
        // Handle boolean-like union types (undefined | false | true, true | false, etc.)
        if (typeof def.type === 'string' && def.type.includes('|')) {
          const parts = def.type.split('|').map(p => p.trim());
          if (parts.every(part => ['true', 'false', 'undefined'].includes(part))) {
            result[propName] = def.defaultValue !== undefined ? def.defaultValue : false;
            break;
          }
        }
        result[propName] = ''; // unknown type => fallback to string
    }
  }

  return result;
}

/**
 * Creates a new config object for the given subtype, using metadata
 */
function createDefaultConfigForSubtype(
  subtype: string,
  metadataObj: Record<string, PropertyMetadata[]>
): { [key: string]: any; id: string; subtype: string } {
  const configTypeKey = findConfigTypeKeyForSubtype(subtype);

  // If no recognized type key, return minimal
  if (!configTypeKey) {
    return { id: '', subtype };
  }

  // Build the object from the property definitions
  const propertyDefs = metadataObj[configTypeKey] || [];
  const baseObj = createObjectFromPropertyDefs(propertyDefs);

  // Ensure we have an empty ID and the chosen subtype
  baseObj.id = '';
  baseObj.subtype = subtype;
  
  // No additional changes needed - defaults are already handled in createObjectFromPropertyDefs

  // Cast so TS knows we have at least .id and .subtype
  return baseObj as { [key: string]: any; id: string; subtype: string };
}

function onFormFieldChanged() {
  // Any time a field changes, let the parent know we have unsaved changes
  emit('config-changed');
}

/**
 * Remove any config entry matching the given BPMN node ID
 */
function removeConfigForNodeId(nodeId: string) {
  const idx = configItems.value.findIndex((c) => c.id === nodeId);
  if (idx !== -1) {
    configItems.value.splice(idx, 1);
    console.log(`Removed config item for nodeId=${nodeId}`);
  }
  emit('config-changed');
}

// We'll rename "saveChanges" to "saveConfig" and NOT call it automatically from a button here:
async function saveConfig() {
  try {
    await props.processorService.saveConfigData(configItems.value);
    console.log('Configuration saved successfully');
    emit('save');
  } catch (error) {
    console.error('Error saving configuration:', error);
  }
}

/**
 * Make this removeConfigForNodeId method callable from the parent.
 */
__expose({
  removeConfigForNodeId,
  saveConfig,
  loadDefaultConfig
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _hoisted_3,
      _createElementVNode("button", {
        class: "close-button",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('close')))
      }, "×")
    ]),
    _createElementVNode("div", _hoisted_4, [
      (!configItems.value || configItems.value.length === 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, " Loading... "))
        : (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _createElementVNode("div", _hoisted_7, [
              _hoisted_8,
              _createElementVNode("select", {
                value: currentSubtype.value,
                onChange: _cache[1] || (_cache[1] = ($event: any) => (onSubtypeChanged($event)))
              }, [
                _hoisted_10,
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(allSubtypes.value, (value, key) => {
                  return (_openBlock(), _createElementBlock("option", {
                    key: key,
                    value: value
                  }, _toDisplayString(key), 9, _hoisted_11))
                }), 128))
              ], 40, _hoisted_9)
            ]),
            (currentSubtype.value !== 'None' && currentItem.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                  _createElementVNode("form", null, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(currentItemFields.value, (field) => {
                      return (_openBlock(), _createBlock(DynamicFormField, {
                        key: field.name,
                        fieldDef: field,
                        value: currentItem.value[field.name],
                        "onUpdate:value": ($event: any) => ((currentItem.value[field.name]) = $event),
                        onChange: onFormFieldChanged
                      }, null, 8, ["fieldDef", "value", "onUpdate:value"]))
                    }), 128))
                  ])
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_13, [
                  _createElementVNode("p", null, "No fields to display for subtype: " + _toDisplayString(currentSubtype.value), 1)
                ]))
          ]))
    ])
  ]))
}
}

})